"use client";

import { useEffect, useRef, useState } from "react";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  HINT,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import HangmanRound from "@/components/tools/games-organizers/hangman-round";
import HangmanSetup, {
  type HangmanSettings,
} from "@/components/tools/games-organizers/hangman-setup";
import HangmanTeams from "@/components/tools/games-organizers/hangman-teams";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  afterGuess,
  DEFAULT_MISSES,
  dealOrder,
  guess,
  type HangmanEntry,
  isLetter,
  keyboardFor,
  parseHangmanList,
  type Round,
  revealLetter,
  startRound,
  startTeams,
  type TeamState,
} from "@/lib/tool-kit/games-organizers/hangman";
import { createRng, newSeed } from "@/lib/tool-kit/worksheets/random";

/** English strings. A translated content file overrides these through `ui`. */
export const HANGMAN_GAME_STRINGS = {
  wordsLabel:
    "Your words — one per line, with an optional clue after a dash or colon",
  wordsHint:
    "Up to {max} words, each with at most {maxLetters} letters. Spaces, hyphens and punctuation are shown from the start.",
  wordsPlaceholder:
    "photosynthesis - How plants make food from light\nchlorophyll - The green pigment in leaves\nmitochondria",
  example:
    "photosynthesis - How plants make food from light\nchlorophyll - The green pigment in leaves\nstomata - Pores on the underside of a leaf\nglucose - The sugar plants make\nrespiration - How cells release energy\nmitochondria - The powerhouse of the cell\nxylem - Tissue that carries water up a plant\nphloem - Tissue that carries sugars",
  loadExample: "Load example",
  wordCount: "one:{count} word ready|other:{count} words ready",
  missesLabel: "Wrong guesses allowed",
  missesOption: "one:{count} miss|other:{count} misses",
  shuffle: "Shuffle the order",
  showClues: "Offer a clue button when a word has one",
  remember: "Remember this list on this device",
  rememberNote:
    "Saved only in this browser, on this device. Nothing is uploaded, and unticking the box deletes the saved list.",
  clearSaved: "Clear saved list",
  start: "Start the game",
  needWords: "Enter at least one word to play.",
  skipped: "Skipped (no letters, or too long): lines {lines}.",
  repeats: "Repeated words were used once: {count}.",
  overflow: "Only the first {max} words are used.",
  heading: "Hangman",
  progress: "Word {current} of {total}",
  score: "Solved {won} · Missed {lost}",
  fullScreen: "Full screen",
  exitFullScreen: "Exit full screen",
  endGame: "End game",
  showClue: "Show clue",
  clueLabel: "Clue:",
  revealLetter: "Reveal a letter (costs a miss)",
  nextWord: "Next word",
  finish: "See the result",
  missesLeft: "one:{count} miss left|other:{count} misses left",
  wrongLetters: "Not in the word:",
  figureLabel: "Gallows with {misses} of {max} misses",
  blank: "blank",
  keyboardLabel: "Letters",
  keyRight: "{letter}, in the word",
  keyWrong: "{letter}, not in the word",
  keyboardHint:
    "Type letters on your keyboard or press the buttons. A word with clues has a clue button.",
  foundLetter:
    "one:{letter}: found {count} time|other:{letter}: found {count} times",
  noLetter: "{letter} is not in the word.",
  won: "Solved! The word was {word}.",
  lost: "Out of misses. The word was {word}.",
  doneHeading: "Game over",
  doneSummary: "You solved {won} of {total}.",
  playAgain: "Play again",
  backToList: "Back to the word list",
  teamsLabel: "Teams",
  teamsHint:
    "Teams take turns. A wrong guess passes the turn, and the team that finds the last letter scores a point.",
  noTeams: "Play as one group",
  teamsOption: "one:{count} team|other:{count} teams",
  teamName: "Team {n}",
  teamTurn: "{team}, it is your turn.",
  points: "one:point|other:points",
  scoreboardLabel: "Team scores",
  finalScores: "Final scores",
} as const;

type Phase = "setup" | "play" | "done";

const STORAGE_KEY = "citationeasy.hangman.v1";

interface Saved {
  text: string;
  misses: number;
}

function readSaved(): Saved | null {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const data = JSON.parse(raw) as Partial<Saved>;
    return typeof data.text === "string"
      ? { text: data.text, misses: Number(data.misses) || DEFAULT_MISSES }
      : null;
  } catch {
    return null;
  }
}

function writeSaved(value: Saved | null): void {
  try {
    if (value) {
      globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(value));
    } else {
      globalThis.localStorage?.removeItem(STORAGE_KEY);
    }
  } catch {
    // Storage can be blocked (private windows, site settings); the game still works.
  }
}

const INITIAL: HangmanSettings = {
  text: "",
  misses: DEFAULT_MISSES,
  teams: 0,
  shuffle: true,
  clues: true,
  remember: false,
};

export default function HangmanGameWidget() {
  const strings = useToolUi(HANGMAN_GAME_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const frame = useRef<HTMLDivElement>(null);
  const [settings, setSettings] = useState<HangmanSettings>(INITIAL);
  const [phase, setPhase] = useState<Phase>("setup");
  const [entries, setEntries] = useState<HangmanEntry[]>([]);
  const [order, setOrder] = useState<number[]>([]);
  const [position, setPosition] = useState(0);
  const [round, setRound] = useState<Round | null>(null);
  const [tally, setTally] = useState({ won: 0, lost: 0 });
  const [teams, setTeams] = useState<TeamState>(startTeams(0));
  const [message, setMessage] = useState("");
  const [wide, setWide] = useState(false);
  const [error, setError] = useState(false);

  const { text, misses, remember } = settings;
  const parsed = parseHangmanList(text, locale);
  const keys = keyboardFor(entries, locale);

  useEffect(() => {
    const saved = readSaved();
    if (saved) {
      setSettings((current) => ({
        ...current,
        text: saved.text,
        misses: saved.misses,
        remember: true,
      }));
    }
  }, []);

  useEffect(() => {
    if (remember && text.trim() !== "") {
      writeSaved({ text, misses });
    }
  }, [remember, text, misses]);

  function update(patch: Partial<HangmanSettings>) {
    setSettings((current) => ({ ...current, ...patch }));
  }

  function begin() {
    if (parsed.entries.length === 0) {
      setError(true);
      return;
    }
    setError(false);
    const list = settings.clues
      ? parsed.entries
      : parsed.entries.map((entry) => ({ ...entry, clue: "" }));
    const deck = settings.shuffle
      ? dealOrder(list.length, createRng(newSeed()))
      : list.map((_, i) => i);
    setEntries(list);
    setOrder(deck);
    setPosition(0);
    setTally({ won: 0, lost: 0 });
    setTeams(startTeams(settings.teams));
    setRound(startRound(list[deck[0] ?? 0] as HangmanEntry, misses, locale));
    setMessage("");
    setPhase("play");
  }

  function settled(next: Round, before: Round) {
    if (next.status === before.status) {
      return;
    }
    setTally((score) => ({
      won: score.won + (next.status === "won" ? 1 : 0),
      lost: score.lost + (next.status === "lost" ? 1 : 0),
    }));
    setMessage(
      t(next.status === "won" ? strings.won : strings.lost, {
        word: next.answer,
      })
    );
  }

  function onGuess(letter: string) {
    if (!round) {
      return;
    }
    const key = letter.toLocaleUpperCase(locale);
    if (!keys.includes(key)) {
      return;
    }
    const result = guess(round, letter, locale);
    if (!result.counted) {
      return;
    }
    setRound(result.round);
    setTeams((current) =>
      afterGuess(current, { found: result.found, status: result.round.status })
    );
    if (result.round.status === round.status) {
      setMessage(
        result.found === 0
          ? t(strings.noLetter, { letter: key })
          : tPlural(strings.foundLetter, locale, result.found, {
              letter: key,
              count: fmt(result.found),
            })
      );
    }
    settled(result.round, round);
  }

  function onReveal() {
    if (!round) {
      return;
    }
    const result = revealLetter(round, createRng(newSeed()));
    if (result.counted) {
      setRound(result.round);
      setMessage("");
      settled(result.round, round);
    }
  }

  function onNext() {
    const nextPosition = position + 1;
    if (nextPosition >= order.length) {
      setPhase("done");
      setRound(null);
      return;
    }
    setPosition(nextPosition);
    setRound(
      startRound(
        entries[order[nextPosition] ?? 0] as HangmanEntry,
        misses,
        locale
      )
    );
    setMessage("");
  }

  const playing = phase === "play" && round?.status === "playing";

  useEffect(() => {
    if (!playing) {
      return;
    }
    function onKey(event: KeyboardEvent) {
      const target = event.target as HTMLElement | null;
      if (
        event.ctrlKey ||
        event.metaKey ||
        event.altKey ||
        target?.closest("input, textarea, select")
      ) {
        return;
      }
      if (event.key.length === 1 && isLetter(event.key)) {
        event.preventDefault();
        onGuess(event.key);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  useEffect(() => {
    function onChange() {
      if (!document.fullscreenElement) {
        setWide(false);
      }
    }
    function onEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setWide(false);
      }
    }
    document.addEventListener("fullscreenchange", onChange);
    window.addEventListener("keydown", onEscape);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      window.removeEventListener("keydown", onEscape);
    };
  }, []);

  async function toggleWide() {
    const next = !wide;
    setWide(next);
    try {
      if (next) {
        await frame.current?.requestFullscreen?.();
      } else if (document.fullscreenElement) {
        await document.exitFullscreen();
      }
    } catch {
      // The overlay still fills the window where the browser refuses real full screen.
    }
  }

  function back() {
    if (wide) {
      toggleWide();
    }
    setPhase("setup");
    setRound(null);
  }

  const frameClass = wide
    ? "fixed inset-0 z-50 overflow-auto bg-background p-4 sm:p-8"
    : "";

  return (
    <div className={`space-y-6 ${frameClass}`} ref={frame}>
      {phase === "setup" ? (
        <HangmanSetup
          error={error}
          onChange={update}
          onClear={() => {
            update({ text: "" });
            writeSaved(null);
          }}
          onRemember={(on) => {
            update({ remember: on });
            if (!on) {
              writeSaved(null);
            }
          }}
          onStart={begin}
          parsed={parsed}
          settings={settings}
          strings={strings}
        />
      ) : null}

      {phase === "play" && round ? (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="space-y-1">
              <h2 className={WIDGET_H2}>{strings.heading}</h2>
              <p className={HINT}>
                {t(strings.progress, {
                  current: fmt(position + 1),
                  total: fmt(order.length),
                })}{" "}
                ·{" "}
                {t(strings.score, {
                  won: fmt(tally.won),
                  lost: fmt(tally.lost),
                })}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                aria-pressed={wide}
                className={BUTTON_SECONDARY}
                onClick={toggleWide}
                type="button"
              >
                {wide ? strings.exitFullScreen : strings.fullScreen}
              </button>
              <button className={BUTTON_SECONDARY} onClick={back} type="button">
                {strings.endGame}
              </button>
            </div>
          </div>
          <HangmanTeams active={playing} strings={strings} teams={teams} />
          <HangmanRound
            canReveal
            isLast={position + 1 >= order.length}
            keys={keys}
            message={message}
            onClue={() => setRound({ ...round, hintShown: true })}
            onGuess={onGuess}
            onNext={onNext}
            onReveal={onReveal}
            round={round}
            strings={strings}
          />
        </div>
      ) : null}

      {phase === "done" ? (
        <div className="space-y-4 text-center">
          <h2 className={WIDGET_H2}>{strings.doneHeading}</h2>
          <p className="text-foreground text-lg">
            {t(strings.doneSummary, {
              won: fmt(tally.won),
              total: fmt(order.length),
            })}
          </p>
          {teams.scores.length >= 2 ? (
            <div className="space-y-2">
              <h3 className={WIDGET_H3}>{strings.finalScores}</h3>
              <HangmanTeams active={false} strings={strings} teams={teams} />
            </div>
          ) : null}
          <div className="flex flex-wrap justify-center gap-3">
            <button className={BUTTON_PRIMARY} onClick={begin} type="button">
              {strings.playAgain}
            </button>
            <button className={BUTTON_SECONDARY} onClick={back} type="button">
              {strings.backToList}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
